/* ************************************************************************** */
/*                                Dependencies                                */
/* ************************************************************************** */

// Lib dependencies
import { useMemo, useState } from 'react';
import { useReactiveVar } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { showNotification } from '@mantine/notifications';

// UI lib components
import { Stack, Container, Alert, MultiSelect } from '@mantine/core';

// UI local components
import ConfirmBar from '@ui-kit/molecules/ConfirmBar';

// Store
import useWorkspace from '@features/Workspace/store/hooks/useWorkspace';
import dialogStoreVar from '@features/Dialog/store/dialogStore';

// Hooks
import useRefetchQuestions from '@features/Support/hooks/useRefetchQuestions';

// Assets
import { IconAlertCircle } from '@tabler/icons-react';

// Local API & Types
import {
  useUpdateQuestionTopicsMutation,
  useTopicsQuery,
} from '@app-types/blackroom-api';
import { DialogQuestionTopicsProps } from './types';

/* ************************************************************************** */
/*                                  Component                                 */
/* ************************************************************************** */

function DialogQuestionTopics({
  questionIds,
  isSelectAll,
  scope,
}: DialogQuestionTopicsProps) {
  /* ********************************** HOOKS ********************************* */

  // Store
  const dialogStore = useReactiveVar(dialogStoreVar);
  const { closeDialog } = dialogStore ?? {};
  const workspace = useWorkspace();

  const [updateQuestionTopics] = useUpdateQuestionTopicsMutation();

  const { data: topicsData } = useTopicsQuery({
    variables: { workspaceId: workspace.id },
  });

  const refetchQuestions = useRefetchQuestions();

  const [selectedTopicIds, setSelectedTopicIds] = useState<string[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Localization
  const { t } = useTranslation();

  const topicOptions = useMemo(
    () =>
      (topicsData?.topics ?? []).map((topic) => ({
        value: topic.id,
        label: topic.name,
      })),
    [topicsData],
  );

  async function onSubmit() {
    try {
      closeDialog?.('dialog-support-question-topics');
      await updateQuestionTopics({
        variables: {
          updateQuestionTopicsInput: {
            topicIds: selectedTopicIds,
            questionIds,
            workspaceId: workspace.id,
            scope,
            isSelectAll,
          },
        },
      });
      refetchQuestions();
    } catch (error) {
      showNotification({
        title: t('Common:errors.genericTitle'),
        message: t('Common:errors.genericMessage'),
        color: 'blackroomRed',
      });
      console.error(error);
    }
  }

  /* ******************************** RENDERING ******************************* */

  return (
    <>
      {/* Invisible overlay to intercept clicks when dropdown is open */}
      {isDropdownOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 250, // Below the dropdown but above the modal
            backgroundColor: 'transparent',
          }}
          onClick={(e) => {
            e.stopPropagation();
            setIsDropdownOpen(false);
          }}
        />
      )}

      <Container w="30vw" fluid>
        <Stack align="stretch" h="100%">
          <MultiSelect
            size="md"
            placeholder={t(
              'Support:PageSupport.supportActions.dialogQuestionTopics.placeholder',
            )}
            data={topicOptions}
            onChange={(ids) => setSelectedTopicIds(ids)}
            styles={{
              input: { display: 'flex' },
            }}
            onDropdownOpen={() => setIsDropdownOpen(true)}
            onDropdownClose={() => setIsDropdownOpen(false)}
          />

          {selectedTopicIds.length === 0 && (
            <Alert
              variant="light"
              color="blackroomRed"
              title={t(
                'Support:PageSupport.supportActions.dialogQuestionTopics.alert.title',
              )}
              icon={<IconAlertCircle />}
              p="xs"
            >
              {t(
                'Support:PageSupport.supportActions.dialogQuestionTopics.alert.text',
              )}
            </Alert>
          )}

          <ConfirmBar
            labelProps={{
              confirm: t('Common:cta.save'),
              cancel: t('Common:cta.cancel'),
            }}
            onConfirm={onSubmit}
            onCancel={() => {
              dialogStore?.closeDialog?.('dialog-support-question-topics');
            }}
          />
        </Stack>
      </Container>
    </>
  );
}

export default DialogQuestionTopics;
